import { AlertCircle, CheckCircle2, XCircle } from "@semo/icons";
import { cn, Button } from "@semo/shared-ui";
import { CancellationRequestDataDtoStatus } from "../types";
import { MessageCardWrapper } from "../layout/MessageCardWrapper";

interface ChatCancellationRequestCardProps {
  message: {
    createdAt: string;
  };
  reason: string;
  rejectionReason?: string;
  status: CancellationRequestDataDtoStatus;
  role: "buyer" | "supplier";
  /** 취소 요청 주체. 미전달(레거시)은 buyer 요청으로 간주 */
  requesterRole?: "buyer" | "supplier";
  sender: "me" | "other";
  tid?: string;
  paymentMethod?: "CARD" | "CASH";
  onApprove?: () => void;
  onReject?: () => void;
  onPrintReceipt?: () => void;
}

export function CancellationRequestCard({
  reason,
  rejectionReason,
  status,
  role,
  requesterRole = "buyer",
  tid,
  paymentMethod = "CARD",
  onApprove,
  onReject,
  onPrintReceipt,
}: ChatCancellationRequestCardProps) {
  const isPending = status === CancellationRequestDataDtoStatus.pending;
  const isApproved = status === CancellationRequestDataDtoStatus.approved;
  const isRejected = status === CancellationRequestDataDtoStatus.rejected;

  // 요청 주체의 "상대방"이 대기 중인 요청을 확인하는 경우에만 승인/반려 버튼 노출
  // (buyer 요청 → supplier에게, supplier 요청 → buyer에게)
  const isCounterparty = role !== requesterRole;
  const showActionButtons = isCounterparty && isPending;
  // 승인된 경우에만 영수증 보기 버튼 노출
  const showReceiptButton = isApproved && !!onPrintReceipt;
  // 결제 수단에 따른 버튼 문구 분기
  const receiptButtonLabel =
    paymentMethod === "CASH" ? "취소 확인서 보기" : "결제 취소 영수증 보기";

  return (
    <MessageCardWrapper
      className={cn(
        "bg-white  border rounded-xl overflow-hidden shadow-sm transition-all",
        isPending
          ? "border-amber-200 "
          : isApproved
            ? "border-red-200 "
            : "border-slate-200 ",
      )}
    >
      {/* Header */}
      <div
        className={cn(
          "px-4 py-3 flex items-center justify-between",
          isPending
            ? "bg-amber-50 "
            : isApproved
              ? "bg-red-50 "
              : "bg-slate-50 ",
        )}
      >
        <div className="flex items-center gap-2">
          {isPending ? (
            <AlertCircle className="w-4 h-4 text-amber-600 " />
          ) : isApproved ? (
            <CheckCircle2 className="w-4 h-4 text-red-600 " />
          ) : (
            <XCircle className="w-4 h-4 text-slate-400" />
          )}
          <span
            className={cn(
              "text-sm font-semibold",
              isPending
                ? "text-amber-700 "
                : isApproved
                  ? "text-red-700 "
                  : "text-slate-600 ",
            )}
          >
            {isPending
              ? "계약 취소 요청"
              : isApproved
                ? "계약 취소 완료"
                : "계약 취소 반려"}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Cancellation Reason (Original Request) */}
        <div
          className={cn(
            "space-y-1",
            isApproved && "opacity-60", // 승인 완료된 경우 원래 사유는 흐리게 표시 (이력 보존용)
          )}
        >
          <span className="text-2xs font-semibold text-slate-400 uppercase tracking-wider">
            {isPending ? "취소 요청 사유" : "취소 사유 (요청)"}
          </span>
          <p
            className={cn(
              "text-sm leading-normal break-words whitespace-pre-wrap",
              isPending ? "text-slate-700" : "text-slate-500",
            )}
          >
            {reason || "사유가 입력되지 않았습니다."}
          </p>
        </div>

        {/* Transaction ID (Optional - Only for approved) */}
        {isApproved && tid && (
          <div className="space-y-1 pt-3 border-t border-red-100/50">
            <span className="text-2xs font-semibold text-red-400 uppercase tracking-wider">
              환불 거래 번호
            </span>
            <p className="text-xs font-mono text-red-600/70 break-all">
              {tid}
            </p>
          </div>
        )}

        {/* Rejection Reason (Only when rejected) */}
        {isRejected && (
          <div className="space-y-2 pt-3 border-t border-slate-100">
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
              <span className="text-2xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">
                반려 사유
              </span>
              <p className="text-sm text-slate-800 font-medium leading-normal break-words whitespace-pre-wrap">
                {rejectionReason || "반려 사유가 입력되지 않았습니다."}
              </p>
            </div>
          </div>
        )}

        {/* Status Help Text (Only when pending) */}
        {isPending && (
          <div className="bg-amber-50/50 rounded-lg p-3 border border-amber-100/50">
            <p className="text-xs text-amber-700/80 text-center font-medium leading-snug">
              {isCounterparty
                ? "내용을 확인하신 후 승인 또는 반려 버튼을 눌러주세요."
                : `${requesterRole === "supplier" ? "발주기관" : "판매자"}의 승인을 기다리고 있습니다.\n승인 시 결제가 취소됩니다.`}
            </p>
          </div>
        )}
      </div>

      {/* Action Area */}
      {showActionButtons && (
        <div className="px-4 pb-4 flex gap-2">
          {onReject && (
            <Button
              onClick={onReject}
              variant="outline"
              className="flex-1 py-2.5 h-auto text-sm font-semibold rounded-xl bg-white border-slate-200 text-slate-700"
            >
              반려하기
            </Button>
          )}
          <Button
            onClick={onApprove}
            variant="destructive"
            className="flex-1 py-2.5 h-auto text-sm font-semibold rounded-xl shadow-sm shadow-red-200"
          >
            취소 승인하기
          </Button>
        </div>
      )}

      {showReceiptButton && (
        <div className="px-4 pb-4">
          <Button
            onClick={onPrintReceipt}
            variant="outline"
            className="w-full py-2.5 h-auto text-sm font-semibold rounded-xl bg-white border-slate-200 text-slate-700"
          >
            {receiptButtonLabel}
          </Button>
        </div>
      )}
    </MessageCardWrapper>
  );
}
